import React, { useEffect, useState } from 'react';
import './VisionBarrierControls.css';

const VisionBarrierControls = ({ open, vision, walls, selectedWall, editing, onEditingChange, onVisionChange, onUpdateWall, onDeleteWall, previewEnabled = false, previewPending = false, previewOriginCount = 0, previewAvailable = true, onPreviewChange, onClose }) => {
  const [distance, setDistance] = useState(vision?.distanceLimit ?? '');
  useEffect(() => setDistance(vision?.distanceLimit ?? ''), [vision?.distanceLimit]);
  if (!open) return null;
  const dynamicVisionEnabled = vision?.mode === 'basic_los';
  const previewDisabled = !previewEnabled && (!dynamicVisionEnabled || !previewAvailable || previewPending);
  const previewStatus = !previewAvailable
    ? 'Player preview is available in a live BattleRoom.'
    : !dynamicVisionEnabled
      ? 'Turn on Basic line of sight to preview the player view.'
      : previewPending
        ? 'Loading the shared player view…'
        : previewEnabled && previewOriginCount === 0
          ? 'Player preview is on, but no player-controlled token provides sight. Assign a token to a player or All Players.'
          : previewEnabled
            ? `Player preview is on. Showing shared sight from ${previewOriginCount} player-controlled ${previewOriginCount === 1 ? 'token' : 'tokens'}. Game Master-only map content is hidden.`
            : 'Player preview is off. The map is showing the normal Game Master view.';
  return (
    <aside className="br-vision-controls" aria-label="Vision and barriers">
      <header><div><strong>Vision & barriers</strong><p>Independent from static Fog and Drawing.</p></div><button type="button" onClick={onClose} aria-label="Close vision controls">Close</button></header>
      <label>Dynamic vision mode
        <select value={vision?.mode || 'off'} onChange={(event) => onVisionChange({ mode: event.target.value, distanceLimit: distance === '' ? undefined : Number(distance) })}>
          <option value="off">Off</option>
          <option value="basic_los">Basic line of sight</option>
        </select>
      </label>
      <label>Distance cap (feet, optional)
        <input type="number" min="1" value={distance} onChange={(event) => setDistance(event.target.value)} onBlur={() => onVisionChange({ mode: vision?.mode || 'off', distanceLimit: distance === '' ? undefined : Number(distance) })} placeholder="No cap" />
      </label>
      <button type="button" className={editing ? 'is-active' : ''} onClick={() => onEditingChange(!editing)} aria-pressed={editing}>{editing ? 'Finish barrier editing' : 'Add/edit barriers'}</button>
      <p className="br-vision-controls__count">{walls.length} of 300 barriers</p>
      <div className="br-vision-controls__toggle-row">
        <span id="br-player-preview-label">Preview as Player</span>
        <button
          type="button"
          role="switch"
          aria-labelledby="br-player-preview-label"
          aria-checked={previewEnabled}
          className={previewEnabled ? 'br-vision-controls__switch is-active' : 'br-vision-controls__switch'}
          disabled={previewDisabled}
          onClick={() => onPreviewChange?.(!previewEnabled)}
        >
          {previewEnabled ? 'On' : 'Off'}
        </button>
      </div>
      <p className="br-vision-controls__preview" role="status" aria-live="polite">{previewStatus}</p>
      {editing && <p className="br-vision-controls__help">Click two map points to add a barrier. Select a barrier to edit its endpoints.</p>}
      {selectedWall && (
        <fieldset>
          <legend>Selected barrier</legend>
          <label><input type="checkbox" disabled={selectedWall.locked === true} checked={selectedWall.blocksVision !== false} onChange={(event) => onUpdateWall({ ...selectedWall, blocksVision: event.target.checked })} /> Blocks vision</label>
          <label><input type="checkbox" disabled={selectedWall.locked === true} checked={selectedWall.blocksMovement !== false} onChange={(event) => onUpdateWall({ ...selectedWall, blocksMovement: event.target.checked })} /> Blocks movement</label>
          <label><input type="checkbox" disabled={selectedWall.locked === true} checked={selectedWall.visibility === 'gm_only'} onChange={(event) => onUpdateWall({ ...selectedWall, visibility: event.target.checked ? 'gm_only' : 'public' })} /> Secret geometry</label>
          <label><input type="checkbox" checked={selectedWall.locked === true} onChange={(event) => onUpdateWall({ ...selectedWall, locked: event.target.checked })} /> Lock endpoints</label>
          <button type="button" className="is-danger" disabled={selectedWall.locked === true} title={selectedWall.locked ? 'Unlock this barrier before deleting it' : 'Delete barrier'} onClick={() => onDeleteWall(selectedWall.wallId)}>Delete barrier</button>
        </fieldset>
      )}
      <p className="br-vision-controls__policy">When both systems are enabled, a player sees only areas revealed by static Fog and visible through basic line of sight.</p>
    </aside>
  );
};

export default VisionBarrierControls;
